// Pricing v2 credits — the frontend mirror of homelistings/credits/catalog.py.
// Every number here must match the API's catalog; nothing else in the SPA may
// hardcode a credit amount or a plan/pack price. Credits are the only pricing
// model: the legacy per-listing checkout and its flag were retired 2026-09-04.

import { storageGet, storageRemove, storageSet } from "../utils/storage";

export const FREE_MONTHLY_CREDITS = 1000;

// Per-action costs (the menu). Keys are the API's artifact/action names.
/** @type {Record<string, number>} */
export const ACTION_COSTS = {
  analysis: 500,
  branded_pdf: 100,
  photo_touchup: 15, // per photo
  deck: 100,
  one_pager: 50,
  social_set: 100,
  listing_review: 400, // withheld from every menu until the product is defined (R5)
  video: 800, // per finished video, however many clips it takes
};

// After the free rerun: analysis re-prepare, per-photo redo, per-clip redo.
/** @type {Record<string, number>} */
export const REGEN_COSTS = { analysis: 250, photo_touchup: 15, video: 800 };

// What can be added to a prepared listing (CREDITS_FLOW_DESIGN v3 §2), in
// menu order. "deck" includes the editable PowerPoint.
export const ADDABLE_ARTIFACTS = ["branded_pdf", "deck", "one_pager", "social_set"];

/** @type {Record<string, string>} */
export const ARTIFACT_LABELS = {
  analysis: "Listing Analysis",
  branded_pdf: "Branded PDF",
  deck: "Seller Presentation",
  one_pager: "One-pager",
  social_set: "Social set",
  photo_touchup: "Photo touch-up",
  video: "Video",
};

// Plain-language sublines shown once, where the choice is made (never as a pitch).
/** @type {Record<string, string>} */
export const ARTIFACT_NOTES = {
  deck: "with the editable PowerPoint",
  social_set: "captions for the listing's social posts",
};

/**
 * @typedef {Object} Plan
 * @property {"agent" | "pro"} product
 * @property {"month" | "year"} interval
 * @property {number} amountCents
 * @property {number} credits
 * @property {"monthly" | "annual"} pool
 */

// Keyed by the API's plan_key ("agent_month"…); typed as a record because the
// key always arrives as a string from /auth/me/.
/** @type {Record<string, Plan>} */
export const PLANS = {
  agent_month: {
    product: "agent",
    interval: "month",
    amountCents: 3900,
    credits: 2500,
    pool: "monthly",
  },
  agent_year: {
    product: "agent",
    interval: "year",
    amountCents: 34900,
    credits: 36000,
    pool: "annual",
  },
  pro_month: {
    product: "pro",
    interval: "month",
    amountCents: 7500,
    credits: 6000,
    pool: "monthly",
  },
  pro_year: {
    product: "pro",
    interval: "year",
    amountCents: 69900,
    credits: 84000,
    pool: "annual",
  },
};

/** @type {Record<string, string>} */
export const PLAN_LABELS = { free: "Free", agent: "Agent", pro: "Pro" };

/** @typedef {{ credits: number, amountCents: number }} Pack */

/** @type {Record<string, Pack>} */
export const PACKS = {
  pack_1500: { credits: 1500, amountCents: 4500 },
  pack_5000: { credits: 5000, amountCents: 12900 },
  pack_12000: { credits: 12000, amountCents: 27900 },
};

export const PACK_EXPIRY_MONTHS = 12;

/** @param {number | string | null | undefined} amount "1,250 credits" */
export const formatCredits = (amount) => `${Number(amount || 0).toLocaleString("en-US")} credits`;

/** @param {number} cents */
export const formatDollars = (cents) => {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
};

// The plan a /auth/me/ plan_key belongs to ("agent_year" -> "agent").
/** @param {string | null | undefined} planKey */
export const planProduct = (planKey) =>
  planKey && planKey !== "free" ? planKey.split("_")[0] : "free";

// The API's fixed 409 paywall contract (views._insufficient_credits_response):
// { error, insufficient_credits: { balance, cost, action } }.
/**
 * @param {any} error An axios error.
 * @returns {{ balance: number, cost: number, action: string } | null}
 */
export const insufficientCreditsFrom = (error) => {
  const data = error?.response?.data;
  if (error?.response?.status !== 409 || !data || typeof data !== "object") return null;
  const block = data.insufficient_credits;
  if (!block || typeof block !== "object") return null;
  return {
    balance: Number(block.balance),
    cost: Number(block.cost),
    action: String(block.action || ""),
  };
};

// --- Client-side rails shared by the credits surfaces (W6) ---
//
// Storage keys. The return stash lets the Stripe legs (/success,
// /subscription-success, /cancel) come back to wherever the paywall sheet
// opened, with the listing underneath untouched. The pack key remembers which
// pack a checkout was for, so /success can say how many credits arrived.
// The resume key is the one-shot hand-off from /success to the page the
// purchase interrupted: the action the agent was about to take, offered again
// without re-entering it. The primer key is the one-time, three-line
// first-spend explainer.
export const CREDITS_RETURN_KEY = "liq_credits_return";
export const CREDITS_PACK_KEY = "liq_credits_pack";
export const CREDITS_RESUME_KEY = "liq_credits_resume";
export const CREDITS_PRIMER_KEY = "liq_credits_primer_seen";

// Menu order for the paywall/pricing rows.
export const PACK_KEYS = ["pack_1500", "pack_5000", "pack_12000"];
export const PLAN_KEYS = ["agent_month", "agent_year", "pro_month", "pro_year"];

/** @param {number | string | null | undefined} amount Just the number, "1,250". */
export const formatCreditsNumber = (amount) => Number(amount || 0).toLocaleString("en-US");

/**
 * The interrupted action a top-up should offer again on return. Each page
 * owns its own shape; only `kind` is required.
 * @typedef {{ kind: string, [key: string]: any }} CreditsResume
 */

/**
 * @typedef {{ path: string, listingUuid?: string, resume?: CreditsResume }} CreditsReturn
 */

/** @param {any} value @returns {CreditsResume | null} */
const normalizeResume = (value) =>
  value && typeof value === "object" && typeof value.kind === "string" && value.kind
    ? /** @type {CreditsResume} */ (value)
    : null;

/** @param {CreditsReturn} stash Remember where a Stripe leg should return to. */
export const stashCreditsReturn = (stash) => {
  if (!stash?.path) return;
  storageSet(CREDITS_RETURN_KEY, JSON.stringify(stash));
};

/** @returns {CreditsReturn | null} Internal paths only — anything else is ignored. */
export const readCreditsReturn = () => {
  const raw = storageGet(CREDITS_RETURN_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const path = typeof parsed?.path === "string" ? parsed.path : "";
    if (!path.startsWith("/") || path.startsWith("//")) return null;
    const resume = normalizeResume(parsed.resume);
    return {
      path,
      ...(parsed.listingUuid ? { listingUuid: String(parsed.listingUuid) } : {}),
      ...(resume ? { resume } : {}),
    };
  } catch {
    return null;
  }
};

export const clearCreditsReturn = () => {
  storageRemove(CREDITS_RETURN_KEY);
};

/**
 * @typedef {{ listingUuid?: string, resume: CreditsResume, balance?: number | null }} CreditsResumeStash
 */

/**
 * Written by /success after a pack purchase, from the return stash's
 * `resume`; read once by the page the purchase interrupted.
 * @param {CreditsResumeStash} stash
 */
export const stashCreditsResume = (stash) => {
  if (!normalizeResume(stash?.resume)) return;
  storageSet(CREDITS_RESUME_KEY, JSON.stringify(stash));
};

/**
 * Read and clear the resume stash. With a listingUuid, only a stash for that
 * listing is returned (a mismatch is dropped, not kept for later).
 * @param {string} [listingUuid]
 * @returns {CreditsResumeStash | null}
 */
export const takeCreditsResume = (listingUuid) => {
  const raw = storageGet(CREDITS_RESUME_KEY);
  if (!raw) return null;
  storageRemove(CREDITS_RESUME_KEY);
  try {
    const parsed = JSON.parse(raw);
    const resume = normalizeResume(parsed?.resume);
    if (!resume) return null;
    const stashedUuid = parsed.listingUuid ? String(parsed.listingUuid) : undefined;
    if (listingUuid && stashedUuid && stashedUuid !== listingUuid) return null;
    const balance = Number(parsed.balance);
    return {
      resume,
      ...(stashedUuid ? { listingUuid: stashedUuid } : {}),
      balance: Number.isFinite(balance) ? balance : null,
    };
  } catch {
    return null;
  }
};
